import { ChangeEventHandler, useState } from 'react'

import classNames from 'classnames'
import { differenceBy, isEqual, take } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { connect, useDataItem } from 'scrivito'

import { buildOrderSubtitle, OrderSublistItem } from './OrderSublistItem'
import { useOrderTabData } from './useOrderTabData'
import { LIST_SIZE } from '../../const'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import { SublistSearchResult } from '@/Atomic/Organisms/RelationalSublist'
import { ScrollablePanel } from '@/Atomic/Templates/ScrollablePanel'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { DataClassCompanyBoundDrawer } from '@/Atomic/Widgets/DataClassDrawer/DataClass.CompanyBoundDrawer'
import { DataItemDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { errorToast } from '@/Components/Toast'
import { ASC_ORDER, DataClassName, DESC_ORDER } from '@/Data/const'
import { DocumentProjectRelation } from '@/Data/DocumentProjectRelation/DocumentProjectRelationDataClass'
import { Order } from '@/Data/Order/OrderDataClass'
import { DataScopeOrder } from '@/Data/types'
import { useCanvas } from '@/hooks/useCanvas'
import { useDataClassIndex } from '@/hooks/useDataClassIndex'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getDataItemTitle, getSchemaName } from '@/utils/dataClassMap'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'

interface OrderTabProps {
  searchInputName: string
  subjectRelation?: 'company' | 'document'
}

export const OrderTab: React.FC<OrderTabProps> = connect(
  ({ searchInputName, subjectRelation = 'company' }) => {
    const { t } = useTranslation()
    const dataItem = useDataItem()
    const subjectId = dataItem?.id() ?? ''
    const isDocument = subjectRelation === 'document'
    const subjectName = isDocument ? null : getDataItemTitle(dataItem)

    const [search, setSearch] = useState('')
    const [order, setOrder] = useState<DataScopeOrder>(DESC_ORDER)

    const {
      drawerItem,
      setDrawerItem,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      openDrawerId,
    } = useDataItemDrawer()
    const { isCanvasOpen, openCanvas, closeCanvas } = useCanvas()
    const { flashId: flashItemId, flash: flashItemIdNow } =
      useFlashSublistItem()

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearch(event.target.value)
    }

    const orderOptions: Option[] = [
      { label: t('common.sort.recentFirst'), value: 'desc' },
      { label: t('common.sort.recentLast'), value: 'asc' },
    ]
    const currentOrderValue = isEqual(order, DESC_ORDER) ? 'desc' : 'asc'
    const selectedOrderOption = orderOptions.find(
      (o) => o.value === currentOrderValue,
    )
    const handleOrderChange = (newValue: SingleValue<Option | unknown>) => {
      const val = (newValue as Option)?.value
      setOrder(val === 'asc' ? ASC_ORDER : DESC_ORDER)
    }

    const { items, hasMore, loadMore, count } = useOrderTabData({
      subjectId,
      search,
      order,
      listSize: LIST_SIZE,
      subjectRelation,
    })

    if (!subjectId) return

    const createDocumentRelation = async (orderId: string) => {
      await DocumentProjectRelation.create({
        document: subjectId,
        project: orderId,
        projectEntity: DataClassName.ORDER,
      })
      flashItemIdNow(orderId)
    }

    const linkOrder = async (orderId: string) => {
      try {
        await createDocumentRelation(orderId)
      } catch (error) {
        errorToast(error as Error)
      }
    }

    const onCreated = (createdId: string) => {
      if (isDocument) {
        void createDocumentRelation(createdId).catch((error) => {
          errorToast(error as Error)
        })
      } else {
        flashItemIdNow(createdId)
      }
    }

    const itemIds = items.map((item) => item.id)
    const currentDrawerId = drawerItem?.id()
    const currentIndex = itemIds.findIndex((id) => id === currentDrawerId)
    const nextId = itemIds[currentIndex + 1]
    const previousId = itemIds[currentIndex - 1]

    const header = (
      <div className="tab-header-wrapper">
        <div className="search-filter">
          <Search
            name={searchInputName}
            onChange={onSearchChange}
            searchWrapperClassName="tab-header-search"
            placeholder={t('search.title')}
          />
          <StringText value={t('common.sortBy')} />
          <SimpleSelect
            name="order-select"
            className="small"
            options={orderOptions}
            value={selectedOrderOption}
            onChange={handleOrderChange}
          />
        </div>
        <div className="tab-header-actions">
          <Button
            title={t('common.create')}
            size="sm"
            iconSize="sm"
            iconColor="white"
            icon="add"
            onClick={openCanvas}
          />
        </div>
      </div>
    )

    const isItemsEmpty = items.length === 0

    return (
      <>
        <Sublist>
          <ScrollablePanel header={header}>
            <div className={classNames({ 'sublist-wrapper': !isItemsEmpty })}>
              {items.map((item) => (
                <OrderSublistItem
                  key={item.id}
                  order={item.order}
                  relation={item.relation}
                  flash={item.id === flashItemId}
                  onOpen={(orderItem) => {
                    setDrawerItem(orderItem)
                    openDrawer()
                  }}
                />
              ))}
              {isDocument && search.trim() && (
                <OrderDocumentSearchResults
                  search={search}
                  linkedItemIds={items.map((item) => item.id)}
                  link={linkOrder}
                  flashItemId={flashItemId}
                />
              )}
              <SublistFooter
                amount={items.length}
                total={count}
                labelKey={getEntityLabelKey(DataClassName.ORDER)}
                onLoadMore={hasMore ? loadMore : undefined}
              />
            </div>
          </ScrollablePanel>
        </Sublist>
        {drawerItem && (
          <DataItemDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
            openDrawerId={openDrawerId}
            nextId={nextId}
            previousId={previousId}
          />
        )}
        <DataClassCompanyBoundDrawer
          dataClass={Order}
          isOpen={isCanvasOpen}
          close={closeCanvas}
          companyId={subjectId}
          companyName={subjectName}
          createTitleKey="pages.orderAdd.title"
          linkedToKey="components.order.linkedTo"
          relationField={isDocument ? undefined : 'customer'}
          onCreated={onCreated}
        />
      </>
    )
  },
)

interface OrderDocumentSearchResultsProps {
  search: string
  linkedItemIds: string[]
  link: (orderId: string) => Promise<void>
  flashItemId?: string | null
}

const OrderDocumentSearchResults = connect(
  ({
    search,
    linkedItemIds,
    link,
    flashItemId,
  }: OrderDocumentSearchResultsProps) => {
    const { dataItems: searchResults } = useDataClassIndex(
      Order,
      { search },
      LIST_SIZE,
    )
    const { schemaAttributes } = getSchemaAttributes(
      getSchemaName(DataClassName.ORDER),
    )
    const searchItems = searchResults.map((item) => ({
      id: item.id(),
      title:
        (item.get('displayTitle') as string) || (item.get('keyword') as string),
      subtitle: buildOrderSubtitle(item, schemaAttributes),
      avatarIcon: 'file-check',
      dataItem: item,
    }))
    const linkedItems = linkedItemIds.map((id) => ({ id }))
    const shownSearchItems =
      search && linkedItemIds.length < LIST_SIZE
        ? take(differenceBy(searchItems, linkedItems, 'id'), LIST_SIZE)
        : []

    return (
      <SublistSearchResult
        items={shownSearchItems}
        link={link}
        flashItemId={flashItemId}
      />
    )
  },
)
